/**
 * Default time range for download commands (EN-1507).
 *
 * Wherever a user enters a download time range, the start/end pickers are
 * pre-filled with the last DEFAULT_DOWNLOAD_RANGE_MINUTES minutes, in UTC,
 * as the 'YYYY-MM-DD HH:mm:ss' string the q-date/q-time pickers hold and
 * DateCommon.IsDateAsStringValid / ConvertDateAsStringToDate parse as UTC.
 *
 * Panels that enforce a shorter maximum interval cap the default to that
 * maximum so the pre-filled range is valid as-is. The cap values below are
 * the single place to change if that decision changes (e.g. set them to
 * DEFAULT_DOWNLOAD_RANGE_MINUTES to always default to 30 minutes).
 */

/** Default download range length, in minutes. */
export const DEFAULT_DOWNLOAD_RANGE_MINUTES = 30;

/** Iridium Offline Download enforces a 10-minute maximum interval. */
export const IRIDIUM_OFFLINE_DOWNLOAD_RANGE_MINUTES = 10;

/** 'RAW - 2 Mins Max' (firmware >= 10.71) enforces a 120-second maximum. */
export const RAW_DOWNLOAD_RANGE_MINUTES = 2;

/** Legacy (firmware < 10) 'RAW - 5 Mins Max' option: default capped to 5 min. */
export const LEGACY_RAW_DOWNLOAD_RANGE_MINUTES = 5;

/** Download option value (RFD FileType) for passages. */
export const DOWNLOAD_OPTION_PASSAGES = 0;

/** Download option value (RFD FileType) for RAW data. */
export const DOWNLOAD_OPTION_RAW = 3;

/**
 * EN-1520: the RAW range caps are enforced on submit, not just used as
 * defaults: exactly the cap is allowed, anything longer is refused. The API
 * enforces the same caps on the web-only endpoints
 * (POST /pigrun/DownloadRecordedRawDataRequest, PUT /commands/{id}/range);
 * POST /commands/queue, shared with the mobile app, allows the AGM's
 * 10-minute limit. Passages and the other file types are uncapped.
 */
export function isRawDownloadRangeOverCap(
  fileType: number | null | undefined,
  start: Date,
  end: Date,
  capMinutes: number
): boolean {
  if (fileType !== DOWNLOAD_OPTION_RAW) return false;
  return end.getTime() - start.getTime() > capMinutes * 60 * 1000;
}

const CAP_MINUTES_IN_WORDS: Record<number, string> = { 2: 'two', 5: 'five' };

/** The inline message shown when a RAW range is longer than the panel's cap. */
export function rawDownloadRangeCapMessage(capMinutes: number): string {
  const minutes = CAP_MINUTES_IN_WORDS[capMinutes] ?? String(capMinutes);
  return (
    'A raw data download request is limited to a ' +
    minutes +
    ' minute maximum interval.'
  );
}

export interface DownloadOption {
  value: number;
}

export interface DownloadRangeDefault {
  start: string;
  end: string;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Formats a Date as the pickers' UTC string 'YYYY-MM-DD HH:mm:ss'. */
export function formatDownloadPickerUtc(date: Date): string {
  return (
    date.getUTCFullYear() +
    '-' +
    pad(date.getUTCMonth() + 1) +
    '-' +
    pad(date.getUTCDate()) +
    ' ' +
    pad(date.getUTCHours()) +
    ':' +
    pad(date.getUTCMinutes()) +
    ':' +
    pad(date.getUTCSeconds())
  );
}

/**
 * Returns { start: now - minutes, end: now } as picker UTC strings.
 * Seconds are kept; milliseconds are dropped by the format.
 */
export function defaultDownloadRange(
  now: Date = new Date(),
  minutes: number = DEFAULT_DOWNLOAD_RANGE_MINUTES
): DownloadRangeDefault {
  const start = new Date(now.getTime() - minutes * 60 * 1000);
  return {
    start: formatDownloadPickerUtc(start),
    end: formatDownloadPickerUtc(now),
  };
}

/**
 * Default range length for a download option: the RAW cap when the RAW option
 * is chosen AND offered by the device, otherwise the 30-minute default.
 */
export function defaultDownloadRangeMinutesForOption(
  option: DownloadOption | null | undefined,
  offeredOptions: readonly DownloadOption[],
  rawCapMinutes: number
): number {
  const isOfferedRaw =
    option?.value === DOWNLOAD_OPTION_RAW &&
    offeredOptions.some((o) => o.value === DOWNLOAD_OPTION_RAW);
  return isOfferedRaw ? rawCapMinutes : DEFAULT_DOWNLOAD_RANGE_MINUTES;
}

/**
 * The option to select when a dialog opens: Passages if offered, else the
 * first offered option. Prevents a RAW choice made for one device carrying
 * over to a device that does not offer RAW.
 */
export function initialDownloadOption<T extends DownloadOption>(
  offeredOptions: readonly T[]
): T | undefined {
  return (
    offeredOptions.find((o) => o.value === DOWNLOAD_OPTION_PASSAGES) ??
    offeredOptions[0]
  );
}

/**
 * True when the fields are empty or still hold the last default we filled,
 * i.e. the user has not typed or picked a range of their own.
 */
export function isUntouchedDownloadRange(
  current: DownloadRangeDefault,
  lastDefault: DownloadRangeDefault | null | undefined
): boolean {
  if (current.start === '' && current.end === '') return true;
  return (
    lastDefault != null &&
    current.start === lastDefault.start &&
    current.end === lastDefault.end
  );
}

/**
 * Returns a fresh default range (now - minutes .. now) when the current range
 * is untouched (empty or equal to the last default), or null to keep what the
 * user entered.
 */
export function refreshedDownloadRange(
  current: DownloadRangeDefault,
  lastDefault: DownloadRangeDefault | null | undefined,
  now: Date,
  minutes: number = DEFAULT_DOWNLOAD_RANGE_MINUTES
): DownloadRangeDefault | null {
  return isUntouchedDownloadRange(current, lastDefault)
    ? defaultDownloadRange(now, minutes)
    : null;
}
